import {
  DepartmentEntryMode,
  type AuthMeInvitationSummary,
  type AuthMeResponse,
} from "@/features/auth/model/auth-types"
import {
  getActiveWorkspaceAccesses,
  getActiveUserBranchAccesses,
  getActiveNormalUserBranchAccesses,
  isSingleBranchWorkspaceAccess,
  resolveBranchSlugFromUserBranchAccess,
} from "@/features/auth/lib/workspace-user-access"
import {
  buildCanonicalBranchAdminUrl,
  buildOrganizationUrl,
} from "@/shared/lib/branch/branch-admin-routing"

export const WORKSPACES_LIST_PATH = "/workspaces"
export const WORKSPACE_BOOTSTRAP_BRANCH_SLUG = "parish"
export const WORKSPACE_BOOTSTRAP_SETTINGS_PATH = "/settings/system"
export const WORKSPACE_SETTINGS_BRANCHES_PATH = "/branches"

export function pickPrimaryWorkspace(authMe: AuthMeResponse) {
  const active = getActiveWorkspaceAccesses(authMe)
  if (active.length === 0) return null

  const defaultWorkspaceId = authMe.user.defaultWorkspaceId
  if (defaultWorkspaceId) {
    const preferred = active.find((access) => access.workspace.id === defaultWorkspaceId)
    if (preferred) return preferred
  }
  return active[0]
}

type WorkspaceAccessEntry = AuthMeResponse["workspaceAccess"][number]

export function resolveBootstrapBranchSlugForWorkspaceAccess(
  access: WorkspaceAccessEntry | null | undefined,
): string | null {
  if (!access) return null
  if (isSingleBranchWorkspaceAccess(access)) return WORKSPACE_BOOTSTRAP_BRANCH_SLUG

  const normals = getActiveNormalUserBranchAccesses(access)
  if (normals.length > 0) {
    const resolved = resolveDefaultBranchSlugForWorkspaceAccess(access)
    if (resolved) return resolved

    const earliest = [...normals].sort(
      (left, right) =>
        new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime(),
    )[0]
    if (earliest?.branch?.slug) return earliest.branch.slug
  }

  const hasSystemBranch = getActiveUserBranchAccesses(access).some(
    (entry) => entry.branch?.branchType === "SYSTEM",
  )
  return hasSystemBranch ? WORKSPACE_BOOTSTRAP_BRANCH_SLUG : null
}

export function resolveDefaultBranchSlugForWorkspaceAccess(
  access: WorkspaceAccessEntry | null | undefined,
): string | null {
  return resolveBranchSlugFromUserBranchAccess(access, null)
}

/** Resolve a concrete target only for a workspace the user explicitly selected. */
export function resolveSwitchTargetBranchSlugForWorkspaceAccess(
  access: WorkspaceAccessEntry | null | undefined,
): string | null {
  const resolved = resolveDefaultBranchSlugForWorkspaceAccess(access)
  if (resolved) return resolved

  const normals = getActiveNormalUserBranchAccesses(access ?? null)
  const earliest = [...normals].sort(
    (left, right) =>
      new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime(),
  )[0]
  if (earliest?.branch?.slug) return earliest.branch.slug

  const hasSystemBranch = getActiveUserBranchAccesses(access ?? null).some(
    (entry) => entry.branch?.branchType === "SYSTEM",
  )
  return hasSystemBranch ? WORKSPACE_BOOTSTRAP_BRANCH_SLUG : null
}

export function buildAdminHref(
  workspaceSlug: string,
  branchSlug: string,
  canonicalAdminPath = "/",
) {
  return buildCanonicalBranchAdminUrl(workspaceSlug, branchSlug, canonicalAdminPath)
}

export function buildWorkspaceSettingsHref(workspaceSlug: string, path = "/branches") {
  return buildOrganizationUrl(workspaceSlug, path)
}

export function getAuthBootstrapInvitations(authMe: AuthMeResponse): AuthMeInvitationSummary[] {
  return [...authMe.pendingInvitations].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  )
}

/** A focused department-only reader's landing: the department they enter, and the path that opens it. */
export type FocusedDepartmentEntry = {
  landingDepartmentId: string | null
  adminPath: string
}

/**
 * Reads the backend-owned entry decision once.
 *
 * Both the route guard and the workspace-switch landing need the department AND the path, and reading
 * `departmentEntry` twice meant re-guarding a value the FOCUSED check had already proven present.
 */
export function resolveFocusedDepartmentEntry(
  access: WorkspaceAccessEntry | null | undefined,
): FocusedDepartmentEntry | null {
  const entry = access?.departmentEntry
  if (entry?.mode !== DepartmentEntryMode.Focused) return null
  const landingDepartmentId = entry.landingDepartmentId
  return {
    landingDepartmentId,
    adminPath: landingDepartmentId ? `/departments/${landingDepartmentId}` : "/departments",
  }
}

/** The context-relative path a focused department-only user belongs on, or null if they are not focused. */
export function resolveFocusedDepartmentAdminPath(
  access: WorkspaceAccessEntry | null | undefined,
): string | null {
  return resolveFocusedDepartmentEntry(access)?.adminPath ?? null
}

/**
 * Absolute href for a focused department-only user's landing, or null when they are not focused.
 *
 * The route guard and the workspace-switch landing both need this, and computing it twice is how they drifted:
 * one of them built the URL from the department's raw branch slug, which duplicates the segment in a mono
 * workspace (`/grace/grace/departments/...`) because mono routes branch-less through the parish alias.
 */
export function resolveFocusedDepartmentLandingHref(access: WorkspaceAccessEntry): string | null {
  const focused = resolveFocusedDepartmentEntry(access)
  if (!focused) return null
  const workspaceSlug = access.workspace?.slug
  if (!workspaceSlug) return null
  const department =
    access.departmentAccess?.find((entry) => entry.departmentId === focused.landingDepartmentId)
    ?? access.departmentAccess?.[0]
  const branchSlug =
    resolveBranchSlugFromUserBranchAccess(access, department?.branchSlug)
    ?? resolveSwitchTargetBranchSlugForWorkspaceAccess(access)
  if (!branchSlug) return null
  return buildAdminHref(workspaceSlug, branchSlug, focused.adminPath)
}

/** Landing for an explicit workspace switch; startup selection remains backend-owned. */
export function resolveWorkspaceLandingHref(
  _authMe: AuthMeResponse,
  access: WorkspaceAccessEntry,
): string {
  const workspaceSlug = access.workspace?.slug
  if (!workspaceSlug) return WORKSPACES_LIST_PATH

  const focusedDepartmentHref = resolveFocusedDepartmentLandingHref(access)
  if (focusedDepartmentHref) return focusedDepartmentHref

  const branchSlug = resolveSwitchTargetBranchSlugForWorkspaceAccess(access)
  if (branchSlug) return buildAdminHref(workspaceSlug, branchSlug, "/")

  return buildWorkspaceSettingsHref(workspaceSlug, WORKSPACE_SETTINGS_BRANCHES_PATH)
}
